import React from 'react'
import {
  ActionWrapper,
  DimmingProfileEditorActions,
  DimmingProfileEditorState,
  PassThroughAction,
} from '../types/presenter.types'
import { LEVEL_INACTIVE } from '../types/dimmingProfile.types'
import { formatMinutes } from '../utils/stepMath'
import { DimmingProfileUiStrings } from '../i18n/dimmingProfileI18n'
import ButtonV2 from '../../../common/components/atom/ButtonV2/Button'

interface StepListProps {
  state: DimmingProfileEditorState
  actions: DimmingProfileEditorActions
  strings: DimmingProfileUiStrings
  readOnly?: boolean
  actionWrapper?: ActionWrapper
}

const cellInput: React.CSSProperties = {
  width: 64,
  height: 26,
  padding: '2px 6px',
  border: '1px solid #d1d5db',
  borderRadius: 4,
  fontSize: 12,
}

// Tabular precise editor for each step, complementing the drag SVG.
const StepList: React.FC<StepListProps> = ({
  state,
  actions,
  strings,
  readOnly = false,
  actionWrapper: ActionSlot = PassThroughAction,
}) => {
  const { steps, selectedStepIndex } = state

  if (steps.length === 0) {
    return (
      <p style={{ fontSize: 13, color: '#6b7280' }}>{strings.noSteps}</p>
    )
  }

  return (
    <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
      <thead>
        <tr style={{ textAlign: 'left', color: '#374151' }}>
          <th style={{ padding: '4px 8px' }}>{strings.time}</th>
          <th style={{ padding: '4px 8px' }}>{strings.level}</th>
          <th style={{ padding: '4px 8px' }}>{strings.inactive}</th>
          <th style={{ padding: '4px 8px' }} />
        </tr>
      </thead>
      <tbody>
        {steps.map((s, i) => {
          const inactive = s.dimming_level === LEVEL_INACTIVE
          const selected = i === selectedStepIndex
          return (
            <tr
              key={i}
              onClick={() => actions.selectStep(i)}
              style={{
                background: selected ? '#eff6ff' : 'transparent',
                cursor: 'pointer',
              }}
            >
              <td style={{ padding: '4px 8px' }}>
                <input
                  type="time"
                  step={600}
                  disabled={readOnly}
                  value={formatMinutes(s.step_time_minutes)}
                  onChange={(e) => {
                    const [hh, mm] = e.target.value.split(':').map(Number)
                    if (Number.isNaN(hh) || Number.isNaN(mm)) return
                    actions.setStepTime(i, hh * 60 + mm)
                  }}
                  style={{ ...cellInput, width: 96 }}
                />
              </td>
              <td style={{ padding: '4px 8px' }}>
                <input
                  type="number"
                  min={0}
                  max={100}
                  disabled={readOnly || inactive}
                  value={inactive ? '' : s.dimming_level}
                  placeholder={inactive ? strings.inactive : ''}
                  onChange={(e) => {
                    const n = parseInt(e.target.value, 10)
                    if (Number.isNaN(n)) return
                    actions.setStepLevel(i, n)
                  }}
                  style={cellInput}
                />
              </td>
              <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                <input
                  type="checkbox"
                  checked={inactive}
                  disabled={readOnly}
                  onChange={() => actions.toggleStepInactive(i)}
                />
              </td>
              <td style={{ padding: '4px 8px' }}>
                <ActionSlot>
                  <ButtonV2
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      if (readOnly) return
                      actions.removeStep(i)
                    }}
                    disabled={readOnly}
                    variant="outlined"
                    color="secondary"
                    size="small"
                  >
                    {strings.removeStep}
                  </ButtonV2>
                </ActionSlot>
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

export default StepList
